import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { CmpsDialog } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import { SecurityScope } from '../../auth/auth.model';
import { takeUntilDestroy } from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { User } from '../../user/user.model';
import { ScorecardTab, ScorecardTabs } from '../main-page/scorecard.state';
import { TranslationKeyMap } from '../scorecard.i18n';

import {
    reloadTabs,
    selectTab,
    selectUser,
    selectUserResult,
} from './sc-header.action';
import scorecardHeaderReducer from './sc-header.reducer';
import { ScorecardHeaderState } from './sc-header.state';

const PAGE_AND_OFFSET = 40;

@Component({
    selector: 'cmps-sc-header',
    templateUrl: './sc-header.component.html',
})
@UseStore<ScorecardHeaderState>(
    'scorecard.header',
    scorecardHeaderReducer.header,
)
export class ScHeaderComponent
    implements OnChanges, OnInit, OnDestroy, StateToProps<ScorecardHeaderState>
{
    state: ScorecardHeaderState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _headerCollapsed: boolean;
    private _headerHeightOg: number;
    private _headerExplicitlyToggled: boolean;

    @ViewChild('scHeaderContainer')
    private readonly _elScHeaderContainer: ElementRef<HTMLElement>;

    @Input() tabs: ScorecardTabs;
    @Input() tab: ScorecardTab;
    @Input() useLiveData: boolean;

    /**
     * (parent is source of truth for tabs)
     * notifies the parent that a new tab with chosen user (userId) needs to be created
     * TODO revert to userId?. will pass back the full user for temporary convenience
     */
    @Output() tabCreate: EventEmitter<User>;

    /**
     * (parent is source of truth for selected-tab)
     * notifies the parent to switch to a specific tab (tabId)
     */
    @Output() tabChange: EventEmitter<string /* tabId */>;

    /**
     * (parent is source of truth for selected-tab)
     * notifies the parent to close(remove) a specific tab (tabId)
     */
    @Output() tabClose: EventEmitter<string /* tabId */>;

    @Output() exportScorecard: EventEmitter<void>;

    @Output() toggleUseLiveData: EventEmitter<boolean>;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    @HostListener('window:scroll', ['$event']) onWindowScroll() {
        const el = document.getElementById('sc-header').getBoundingClientRect();

        // handle with sticky behavior on ie11
        if (window.pageYOffset > 0) {
            this._beSticky();
        } else {
            this._removeSticky();
        }

        if (this._headerExplicitlyToggled) {
            return;
        }

        this._headerCollapsed =
            window.pageYOffset > el.height + PAGE_AND_OFFSET;
    }

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _dialog: CmpsDialog,
    ) {
        this.tabCreate = new EventEmitter();
        this.tabChange = new EventEmitter();
        this.tabClose = new EventEmitter();
        this.exportScorecard = new EventEmitter();
        this.toggleUseLiveData = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('tabs')) {
            this._reloadTabs();
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('tab')) {
            this._selectTab();
        }
    }

    ngOnInit(): void {
        this._reloadTabs();
        this._selectTab();
    }

    ngOnDestroy(): void {
        this.tabCreate.complete();
        this.tabChange.complete();
        this.tabClose.complete();
        this.exportScorecard.complete();
    }

    stateToProps(prevState?: ScorecardHeaderState): void {
        if (prevState.userSelectOpts.open !== this.state.userSelectOpts.open) {
            if (!this.state.userSelectOpts.open) {
                if (this.state.userSelectOpts.user != null) {
                    this.tabCreate.emit(this.state.userSelectOpts.user);
                }
            } else {
                const ref = this._dialog.open<
                    SearchUserContainerComponent,
                    any,
                    User
                >(SearchUserContainerComponent, {
                    data: {
                        userId: this.state.userSelectOpts.userId,
                        scope: SecurityScope.SCORECARD,
                    },
                });
                ref.result
                    .pipe(
                        tap((user) => {
                            useDispatcher(this, selectUserResult({ user }));
                        }),
                        takeUntilDestroy(this),
                    )
                    .subscribe();
            }
        }
    }

    /** @private */ _onSelectUserClick(): void {
        useDispatcher(
            this,
            selectUser({ userId: this.state.selectedTab.userId }),
        );
    }

    /** @private */ _onTabChange(tabId: string): void {
        this.tabChange.emit(tabId);
    }

    /** @private */ _onTabClose(tabId: string): void {
        this.tabClose.emit(tabId);
    }

    /** @private */ _onExportScorecard(): void {
        this.exportScorecard.emit();
    }

    /** @private */ _onToggleExpandClick(): void {
        this._headerExplicitlyToggled = true;
        this._headerCollapsed = !this._headerCollapsed;
    }

    /** @private */ _onToggleUseLiveData(useLiveData: boolean): void {
        this.toggleUseLiveData.emit(useLiveData);
    }

    private _reloadTabs(): void {
        if (this.tabs) {
            useDispatcher(this, reloadTabs({ tabs: this.tabs }));
        }
    }

    private _selectTab(): void {
        if (this.tab) {
            useDispatcher(this, selectTab({ tab: this.tab }));
        }
    }

    private _beSticky(): void {
        // add fix position in header container
        this._elScHeaderContainer.nativeElement.classList.add(
            'sc-header-stickied',
        );
        // to avoid a scroll bug when user scroll down, add same margin in sc container
        const el =
            this._elScHeaderContainer.nativeElement.getBoundingClientRect();

        if (this._headerHeightOg == null) {
            this._headerHeightOg = el.height;
        }

        const height = this._headerExplicitlyToggled
            ? el.height
            : this._headerHeightOg;
        this._el.nativeElement.parentElement.style.marginTop = `${height}px`;
    }

    private _removeSticky(): void {
        this._elScHeaderContainer.nativeElement.classList.remove(
            'sc-header-stickied',
        );

        this._el.nativeElement.parentElement.style.marginTop = '';

        this._headerHeightOg = null;
    }
}
